import React, { useState, useEffect, useRef } from 'react';
import { useTrackingContext } from 'src/components/TrackingContext';
import { YoutubeVideo } from './Video.types';

type Props = {
  open?: boolean;
  video: YoutubeVideo;
};

let loadYT;

export const YoutubePlayer: React.FC<Props> = ({ open = true, video }): JSX.Element => {
  const { trackInteraction } = useTrackingContext();
  const [player, setPlayer] = useState();
  const trackingRef = useRef(null);
  trackingRef.current = trackInteraction;
  const playerIsReady = useRef(false);
  const isPlayingRef = useRef(false);
  const titleRef = useRef<string>(video.videoId);
  const timerRef = useRef(null);
  const percentagesComplete = [75, 50, 25];
  const percentagesCompleteRef = useRef(percentagesComplete);

  const handlePlayerReady = (event) => {
    titleRef.current = event.target.videoTitle;
    playerIsReady.current = true;
  };

  const handlePercentComplete = (event) => {
    const duration = parseInt(event.target.getDuration());
    const currentTime = parseInt(event.target.getCurrentTime());
    const currentPercent = (currentTime / duration) * 100;
    percentagesCompleteRef.current.length &&
      percentagesCompleteRef.current.some((targetPercent, targetIndex, targetArray) => {
        if (currentPercent >= targetPercent) {
          trackingRef.current(
            undefined,
            undefined,
            { video_name: `Video ${titleRef.current}` },
            `video_${targetPercent}`
          );
          percentagesCompleteRef.current.splice(targetIndex, targetArray.length);
          return true;
        }
      });
  };

  const handlePlayerStateChange = (event) => {
    switch (event.data) {
      case YT.PlayerState.PLAYING: // eslint-disable-line
        if (!isPlayingRef.current) isPlayingRef.current = true;
        break;
      case YT.PlayerState.PAUSED: // eslint-disable-line
        if (isPlayingRef.current) isPlayingRef.current = false;
        break;
      case YT.PlayerState.ENDED: // eslint-disable-line
        if (isPlayingRef.current) isPlayingRef.current = false;
        trackingRef.current(undefined, undefined, { video_name: `Video ${titleRef.current}` }, 'video_complete');
        break;
      default:
    }

    if (event.data === 1) {
      timerRef.current = setInterval(() => handlePercentComplete(event), 1000);
    } else {
      clearInterval(timerRef.current);
    }
  };

  useEffect(() => {
    if (!loadYT) {
      loadYT = new Promise((resolve) => {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        window.onYouTubeIframeAPIReady = () => resolve(window.YT); // eslint-disable-line
      });
    }

    loadYT.then((YT) => {
      const ytPlayer = new YT.Player(`player-${video.videoId}`, {
        host: 'https://www.youtube-nocookie.com',
        videoId: video.videoId,
        height: video.height,
        width: video.width,
        playerVars: video.playerVars,
        events: {
          onReady: handlePlayerReady,
          onStateChange: handlePlayerStateChange,
        },
      });
      setPlayer(ytPlayer);
    });
  }, []);

  useEffect(() => {
    if (player && playerIsReady.current && open) {
      /**
       * Note: If player is not in a modal & autoplay is on (it is on by default),
       * Chrome will not autoplay videos unless they are muted as stated here:
       * https://developer.chrome.com/blog/autoplay/
       */
      if (player.isMuted()) player.unMute(); // eslint-disable-line
      player.playVideo(); // eslint-disable-line
      trackInteraction('video click', 'load video', { video_name: `Video ${titleRef.current}` }, 'video_start');
    }

    if (!open && isPlayingRef.current) {
      isPlayingRef.current = false;
      player.stopVideo(); // eslint-disable-line
    }

    // Reset percentagesCompleteRef array
    if (!open) percentagesCompleteRef.current = percentagesComplete;
  }, [open]);

  return <div id={`player-${video.videoId}`}></div>;
};
